import { SidebarItemType } from 'components/sidebar/types';
import { createContext, useContext } from 'react';
import { CoKitchenType } from 'models/cokitchen.model';
import { BranchAddressType } from 'models/branch-address.model';
import { DashboardMonthlyStatsType } from 'models/statements.model';
import { PaymentCardType, PaystackPropsType } from 'models/payment.model';
import { CompaniesListType, OnboardingType } from 'models/company.model';
import { MemberListType } from 'models/member.model';
import { LocationObjectType } from 'models/location.model';
import { CalculatedOrderType } from 'models/order.model';
import { CompanyWalletType } from 'models/wallet.model';

export type PaymentActionType = 'paystack' | 'transfer' | 'wallet';

export type DashboardContextType = {
  monthlyStat: DashboardMonthlyStatsType;
  setMonthlyStat: (item: DashboardMonthlyStatsType) => void;
  refetchMonthlyStat: () => void;
  monthlyStatLoading: boolean;
  setMonthlyStatLoading: (item: boolean) => void;
  paymentCards: PaymentCardType[];
  setPaymentCards: (item: PaymentCardType[]) => void;
  refetchPaymentCards: () => void;
  activePaymentCard: PaymentCardType | null;
  setActivePaymentCard: (item: PaymentCardType | null) => void;
  paymentView: 'methods' | 'pay';
  setPaymentView: (item: 'methods' | 'pay') => void;
  paymentAmount: string;
  setPaymentAmount: (item: string) => void;
  paymentType: PaymentActionType | null;
  setPaymentType: (item: PaymentActionType | null) => void;
  paymentLoading: boolean;
  setPaymentLoading: (item: boolean) => void;
  showMobileSidebar: boolean;
  activeCokitchen: CoKitchenType;
  setActiveCokitchen: (cokitchen: CoKitchenType) => void;
  setShowMobileSidebar: (value: boolean) => void;
  showFeedbackModal: boolean;
  setShowFeedbackModal: (value: boolean) => void;
  isBackLink: boolean;
  setIsBackLink: (value: boolean) => void;
  backLinkTitle: string;
  setBackLinkTitle: (value: string) => void;
  hideSidebar: boolean;
  setHideSidebar: (value: boolean) => void;
  backLink: string;
  setBackLink: (value: string) => void;
  needHelp: boolean;
  setNeedHelp: (value: boolean) => void;
  activeCompany: CompaniesListType;
  setActiveCompany: (value: CompaniesListType) => void;
  members: MemberListType[];
  setMembers: (item: MemberListType[]) => void;
  refetchMembers: () => void;
  companyList: CompaniesListType[];
  setCompanyList: (arg0: CompaniesListType[]) => void;
  paystackProps: PaystackPropsType;
  setPaystackProps: (value: PaystackPropsType) => void;
  refetchCompanyList: () => Promise<void>;
  refetchSpecificCompany: (id: string) => Promise<void>;
  refetchActiveCompany: () => Promise<void>;
  sidebarMenu: SidebarItemType[];
  setSidebarMenu: (value: SidebarItemType[]) => void;
  basketValue: number;
  setBasketValue: (value: number) => void;
  addressListLoading: boolean;
  setAddressListLoading: (value: boolean) => void;
  savedAddressList: LocationObjectType[];
  setSavedAddressList: (value: LocationObjectType[]) => void;
  refetchSavedAddressList: () => Promise<void>;
  isBasketDisabled: boolean;
  setIsBasketDisabled: (value: boolean) => void;
  defaultAddress: LocationObjectType | null;
  setDefaultAddress: (value: LocationObjectType | null) => void;
  refetchDefaultAddress: () => Promise<void>;
  defaultAddressLoading: boolean;
  cokitchenList: CoKitchenType[];
  calculatedOrder: CalculatedOrderType;
  setCalculatedOrder: (arg: CalculatedOrderType) => void;
  companyWalletDetails: CompanyWalletType | undefined;
  refetchCompanyWalletDetails: () => Promise<void>;
  companyBudget: string;
  branchAddresses: BranchAddressType[];
  branchAddressesLoading: boolean;
  refetchBranchAddresses: () => Promise<void>;
  onboarding: OnboardingType[];
  setOnboarding: (value: OnboardingType[]) => void;
  refetchOnboarding: () => Promise<void>;
};

export const DashboardContext = createContext({} as DashboardContextType);

const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  return context;
};

export default useDashboardContext;
